import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  onIdTokenChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { firebaseAuth } from "../lib/firebase";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_ENDPOINT = `${API_URL}/api/auth/session`;

const FIREBASE_ERROR_MESSAGES = {
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/missing-password": "Please enter your password.",
  "auth/invalid-credential": "Invalid email or password.",
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Invalid email or password.",
  "auth/email-already-in-use": "This email is already registered.",
  "auth/weak-password": "Password should be at least 6 characters.",
  "auth/too-many-requests": "Too many attempts. Please try again later.",
};

const setAuthHeader = (idToken) => {
  if (idToken) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  const parseError = (error, fallbackMessage) => {
    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error?.code && FIREBASE_ERROR_MESSAGES[error.code]) {
      return FIREBASE_ERROR_MESSAGES[error.code];
    }
    return fallbackMessage;
  };

  const syncSession = useCallback(async (idToken, profileUpdates = {}) => {
    if (!API_URL) {
      throw new Error("REACT_APP_BACKEND_URL is not configured");
    }

    setAuthHeader(idToken);
    const response = await axios.post(SESSION_ENDPOINT, {
      id_token: idToken,
      ...profileUpdates,
    });
    setToken(idToken);
    setUser(response.data.user);
    return response.data.user;
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        setAuthHeader(null);
        setToken(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const idToken = await firebaseUser.getIdToken();
        await syncSession(idToken);
      } catch (error) {
        console.error("Auth sync failed:", error);
        await signOut(firebaseAuth);
        setAuthHeader(null);
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [syncSession]);

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);
      const idToken = await result.user.getIdToken(true);

      return await syncSession(idToken, {
        name: result.user.displayName || undefined,
        photo_url: result.user.photoURL || undefined,
      });
    } catch (error) {
      throw new Error(parseError(error, "Google sign-in failed. Please try again."));
    }
  };

  const logout = () => {
    signOut(firebaseAuth).catch((error) => {
      console.error("Firebase sign out failed:", error);
    });
    setAuthHeader(null);
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        loginWithGoogle,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
